import apiClient from '@/client/api';
import { AnswerModel } from '@/components/decision-tree/hooks';
import { UserQuestionnaireType } from '@/components/decision-tree/types';

// Types based on the controller responses
export type PpraSurveySection = {
  id: string;
  title: string;
  description?: string;
  surveyItems: PpraSurveyItem[];
};

export type PpraSurveyItem = {
  id: string;
  question: string;
  type: string;
  options?: PpraSurveyItemOption[];
  required?: boolean;
};

export type PpraSurveyItemOption = {
  id: string;
  text: string;
  value: string;
};

export type PpraSurveyDto = {
  id: string;
  name: string;
  version: string;
  slug: string;
  structure: {
    sections: PpraSurveySection[];
  };
  currentStep: number;
  totalSteps: number;
  status: string;
  lastAnsweredQuestionId?: string;
};

// SessionSurveyDto extends UserQuestionnaireType with additional properties
export type SessionSurveyDto = UserQuestionnaireType & {
  id: string;
  surveyId: string;
  currentStep: number;
  totalSteps: number;
  lastAnsweredQuestionId?: string;
};

/**
 * Create a new PPRA survey session
 */
async function createSurvey(version: string): Promise<SessionSurveyDto> {
  const response = await apiClient.post(`/session/me/ppra/${version}`);
  return response.data;
}

/**
 * Save an answer to a PPRA survey question
 * @param sessionSurveyId - The ID of the session survey
 * @param surveyItemId - The ID of the survey item being answered
 * @param answer - The answer data
 * @returns The updated session survey
 */
async function saveAnswer(
  sessionSurveyId: string,
  surveyItemId: string,
  answer: AnswerModel
): Promise<SessionSurveyDto> {
  if (!sessionSurveyId || !surveyItemId) {
    throw new Error('Missing required parameters: sessionSurveyId or surveyItemId');
  }
  
  const response = await apiClient.put(`/session/me/ppra/${sessionSurveyId}/question/${surveyItemId}`, answer);
  
  if (!response || !response.data) {
    console.error('Invalid response from PPRA API', {
      sessionSurveyId,
      surveyItemId,
      answer,
      response,
      responseType: typeof response,
      hasData: response?.data !== undefined
    });
    throw new Error('Invalid response from server: missing data');
  }
  
  return response.data;
}

export type PpraSurveyResultsDto = {
  sessionId: string;
  results: {
    riskScore: number;
    factorsIncreasingRiskCount: number;
    factorsDecreasingRiskCount: number;
  };
};

export default {
  createSurvey,
  saveAnswer,
};
